import { Check } from "lucide-react";

import { STAGES } from "pages/CaseTracker/stages";
import { classNames } from "utils/helpers";
import { cardClassName } from "./Card";

// The stages a case moves through in order; rejected and withdrawn cases leave this path.
const STEPS = [
  { states: ["SUBMITTED"], label: "Submitted" },
  ...STAGES.filter(
    (stage) =>
      !stage.states.includes("REJECTED") && !stage.states.includes("WITHDRAWN"),
  ).map(({ states, label }) => ({ states, label })),
];

interface CaseTimelineProps {
  state: string;
}

const CaseTimeline: React.FC<CaseTimelineProps> = ({ state }) => {
  const currentIndex =
    state === "COMPLETED"
      ? STEPS.length
      : STEPS.findIndex((step) => step.states.includes(state));

  if (currentIndex < 1) {
    return null;
  }

  return (
    <section className={classNames(cardClassName, "bg-gradient-brand-b p-6")}>
      <ol
        aria-label="Case progress"
        className="flex flex-col gap-4 sm:flex-row sm:gap-2"
      >
        {STEPS.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLast = index === STEPS.length - 1;

          return (
            <li
              key={step.label}
              aria-current={isCurrent ? "step" : undefined}
              className={classNames(
                "flex gap-3 sm:flex-col sm:gap-2",
                !isLast && "sm:flex-1",
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={classNames(
                    "flex size-7 shrink-0 items-center justify-center rounded-full border-2",
                    isDone && "border-success bg-success text-white",
                    isCurrent && "border-success bg-success-soft",
                    !isDone && !isCurrent && "border-border bg-background",
                  )}
                >
                  {isDone && <Check className="size-4" />}
                  {isCurrent && (
                    <span className="relative flex size-2">
                      <span className="absolute inset-0 rounded-full bg-success motion-safe:animate-ping" />
                      <span className="relative size-2 rounded-full bg-success" />
                    </span>
                  )}
                </span>
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className={classNames(
                      "hidden h-0.5 flex-1 rounded-full sm:block",
                      isDone ? "bg-success" : "bg-border",
                    )}
                  />
                )}
              </div>
              <div>
                <p
                  className={classNames(
                    "text-sm font-medium",
                    isDone || isCurrent
                      ? "text-brand-copy-primary"
                      : "text-muted-foreground",
                  )}
                >
                  {step.label}
                  {isDone && <span className="sr-only"> (done)</span>}
                </p>
                {isCurrent && (
                  <p className="text-xs text-warning">In progress</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default CaseTimeline;
